import { doc, setDoc, getDoc, getDocs, collection, query, where, orderBy, updateDoc, writeBatch, serverTimestamp, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Order, OrderRoute, OrderRoutePoint, OrderCargo } from './types';
import { FreightOffer, Location, TimeWindow } from '../../types/exchange';
import { Bid } from '../../types/bid';
import { generateOrderId } from './idGeneratorService';

/** Customer (shipper) block the assigned driver must see in full — company
 *  name + a callable contact. The PUBLIC exchange deliberately hides company
 *  identity from other tenants; the driver of the winning carrier is the one
 *  audience that needs it complete. */
export interface OrderCustomer {
  companyName: string;
  contactName?: string;
  phone?: string;
  email?: string;
}

/** Denormalized job details copied onto the order for the Driver app to render. */
export interface OrderDenormalization {
  route: OrderRoute;
  cargo: OrderCargo;
  notes: string;
  customer: OrderCustomer;
}

/** Payload for assigning a driver + vehicle to an order. companyId is NEVER part
 *  of this — it must stay immutable on update (Firestore rules require it). */
export interface AssignDriverVehicleInput {
  driverId: string;
  vehicleId: string;
  driverName?: string;
  vehiclePlate?: string;
  route?: OrderRoute;
  cargo?: OrderCargo;
  notes?: string;
  customer?: OrderCustomer;
  /**
   * The vehicle the order was assigned to BEFORE this call, if any. On a re-assign
   * to a different truck it is cleared (`currentDriverId`/`currentOrderId` → null)
   * so no truck ever shows a stale driver on the fleet map / vehicles page.
   */
  previousVehicleId?: string;
}

/** Build a route point from an offer location + time window, omitting every
 *  undefined sub-field so the written document stays Firestore-valid. */
const buildRoutePoint = (loc: Location, time?: TimeWindow): OrderRoutePoint => {
  const point: OrderRoutePoint = {
    city: loc.city,
    country: loc.country,
    postalCode: loc.postalCode,
  };
  if (loc.address) point.address = loc.address;
  if (loc.coordinates) {
    point.lat = loc.coordinates.lat;
    point.lng = loc.coordinates.lng;
  }
  if (time?.date) point.date = time.date;
  if (time?.startTime) point.startTime = time.startTime;
  if (time?.endTime) point.endTime = time.endTime;
  return point;
};

/**
 * Denormalize the parts of a freight offer (+ winning bid) the Driver app shows:
 * `route`, `cargo`, `notes`. Pure + side-effect free so it is unit-testable and
 * reusable both when the order is first materialized and when a driver is assigned.
 */
export const buildOrderDenormalization = (
  offer: FreightOffer,
  bid?: Bid | null,
): OrderDenormalization => {
  const route: OrderRoute = {
    pickup: buildRoutePoint(offer.loadingLocation, offer.loadingTime),
    delivery: buildRoutePoint(offer.unloadingLocation, offer.unloadingTime),
  };

  const cargo: OrderCargo = {
    description: offer.loadDescription || '',
    weight: offer.weight ?? 0,
    loadingMeters: offer.loadingMeters ?? 0,
    loadType: offer.loadType,
  };
  if (offer.volume != null) cargo.volume = offer.volume;
  if (offer.stackable != null) cargo.stackable = offer.stackable;
  if (offer.temperatureControlled != null) cargo.temperatureControlled = offer.temperatureControlled;
  if (offer.hazardous != null) cargo.hazardous = offer.hazardous;

  const notes = [offer.additionalInfo, offer.specialRequirements, bid?.message]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' • ');

  // Customer (shipper) identity + callable contact for the assigned driver.
  const contact = offer.contactPerson ?? offer.shipper?.contactPersons?.[0];
  const customer: OrderCustomer = {
    companyName: (offer.shipper?.name || '').trim(),
  };
  if (contact?.name?.trim()) customer.contactName = contact.name.trim();
  if (contact?.phone?.trim()) customer.phone = contact.phone.trim();
  if (contact?.email?.trim()) customer.email = contact.email.trim();

  return { route, cargo, notes, customer };
};

export class OrderService {
  async createOrder(orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>): Promise<Order> {
    try {
      const customId = await generateOrderId();
      const orderRef = doc(db, 'orders', customId);
      const order: Order = {
        ...orderData,
        id: customId,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };

      await setDoc(orderRef, order);
      return order;
    } catch (error) {
      console.error('Chyba pri vytváraní objednávky:', error);
      throw new Error('orders.errors.create_failed');
    }
  }

  async getOrder(orderId: string): Promise<Order | null> {
    try {
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (orderDoc.exists()) {
        return { ...(orderDoc.data() as Order), id: orderDoc.id };
      }
      return null;
    } catch (error) {
      // A permission-denied here is an expected outcome, not a failure: the
      // viewer requested an order they have no read grant on (e.g. a shipper
      // checking whether their accepted freight bid materialised into a
      // carrier-owned order under the tenant-isolation rules). Treat it as
      // "no accessible order" and stay quiet — only real errors are logged.
      if ((error as { code?: string })?.code === 'permission-denied') {
        return null;
      }
      console.error('Chyba pri načítaní objednávky:', error);
      throw new Error('orders.errors.load_failed');
    }
  }

  async updateOrder(orderId: string, updates: Partial<Order>): Promise<void> {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Chyba pri aktualizácii objednávky:', error);
      throw new Error('orders.errors.update_failed');
    }
  }

  /**
   * Real-time company orders for the fleet map — lets the dispatcher map each
   * located vehicle to its live transport phase (`order.tracking.driverStatus`).
   * Uses the same `companyId`-only filter as `getOrdersByCompany` (no new index;
   * the `orders` list rule allows `sameCompany`); callers aggregate client-side.
   * Returns the Firestore unsubscribe function.
   */
  subscribeToCompanyOrders(
    companyId: string,
    onData: (orders: Order[]) => void,
    onError?: (err: Error) => void,
  ): () => void {
    if (!companyId) {
      onData([]);
      return () => {};
    }
    const q = query(collection(db, 'orders'), where('companyId', '==', companyId));
    return onSnapshot(
      q,
      (snap) => onData(snap.docs.map((d) => ({ ...(d.data() as Order), id: d.id }))),
      (error) => {
        console.error('Chyba pri odbere objednávok firmy:', error);
        onError?.(new Error('orders.errors.load_company_failed'));
      },
    );
  }

  async getOrdersByCompany(companyId: string): Promise<Order[]> {
    try {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(ordersQuery);
      return querySnapshot.docs.map(doc => doc.data() as Order);
    } catch (error) {
      console.error('Chyba pri načítaní objednávok firmy:', error);
      throw new Error('orders.errors.load_company_failed');
    }
  }

  /**
   * Get (or lazily create) the order that backs an accepted freight offer.
   *
   * Until a driver flow exists, accepting a bid only completes the offer — no
   * `orders/` document is written. This materializes one on demand using the
   * offer id as the (deterministic, idempotent) order id, so the dispatcher has
   * something concrete to assign a driver + vehicle to. The document carries the
   * denormalized route/cargo/notes the Driver app reads.
   *
   * OWNERSHIP (T13): the order belongs to the CARRIER (the winning bidder) —
   * `companyId = bid.bidderCompanyId` — because the carrier assigns their own
   * driver + vehicle. The shipper (offer creator) keeps read-only visibility via
   * the denormalized `ownerCompanyId = offer.companyId` (firestore.rules orders
   * read). The caller MUST therefore be a member of the carrier company (the
   * Firestore create rule = sameCompany on the written companyId).
   *
   * `companyId` is the authenticated caller's company (AuthContext) — used only
   * as a presence guard; the written tenant id comes from the bid.
   *
   * LEGACY: orders created before this change are creator-owned (companyId =
   * shipper, no ownerCompanyId). The offerId-keyed lookup below returns them
   * unchanged — readers must tolerate both ownership shapes.
   */
  async ensureOrderForOffer(
    offer: FreightOffer,
    bid: Bid,
    companyId: string,
  ): Promise<Order> {
    if (!companyId) throw new Error('assign.errors.no_company');
    const orderRef = doc(db, 'orders', offer.id);

    try {
      const snap = await getDoc(orderRef);
      if (snap.exists()) {
        return { ...(snap.data() as Order), id: snap.id };
      }
    } catch {
      // Fall through to create — a read miss/permission edge shouldn't block assign.
    }

    const { route, cargo, notes, customer } = buildOrderDenormalization(offer, bid);
    const carrierCompanyId = bid.bidderCompanyId || bid.bidderId;
    const order: Record<string, unknown> = {
      id: offer.id,
      transportId: offer.id,
      offerId: offer.id,
      acceptedBidId: bid.id,
      companyId: carrierCompanyId, // carrier owns the order (assigns driver/vehicle)
      carrierId: carrierCompanyId,
      carrierName: bid.bidderName,
      status: 'accepted',
      price: bid.agreedPrice ?? bid.proposedPrice,
      currency: bid.currency,
      paymentStatus: 'pending',
      timeline: { acceptedAt: serverTimestamp() },
      tracking: {},
      route,
      cargo,
      customer,
      notes,
      assignedDriverId: null,
      assignedVehicleId: null,
      currentVehicleId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    // Denormalized shipper company — grants the offer creator read-only
    // visibility (rules). Omitted when the offer carries no companyId so the
    // written document stays Firestore-valid (no undefined field values).
    if (offer.companyId) order.ownerCompanyId = offer.companyId;

    try {
      await setDoc(orderRef, order);
    } catch (error) {
      console.error('Chyba pri vytváraní objednávky pre ponuku:', error);
      throw new Error('assign.errors.create_failed');
    }
    return order as unknown as Order;
  }

  /**
   * Assign a driver + vehicle to an order and denormalize the job details the
   * Driver app renders. Writes the assignment fields onto the order (never
   * `companyId`, which must stay immutable per the Firestore update rule) AND
   * mirrors the link onto the vehicle (`currentDriverId`/`currentOrderId`) so the
   * fleet map, the vehicles page and `updateVehicleLocation`'s authz can resolve
   * the driver — and so the Driver app may read its own vehicle (vehicles get
   * rule). All writes go through ONE atomic `writeBatch` so the order and the
   * vehicle(s) can never diverge.
   *
   * On a re-assign to a DIFFERENT truck, the previously-linked vehicle is cleared
   * (`currentDriverId`/`currentOrderId` → null) so a truck never shows a stale
   * driver. Resets `driverActions` so a re-assignment clears any prior
   * accept/reject. Status moves to `assigned`; the Driver app then surfaces
   * Accept/Reject.
   */
  async assignDriverVehicle(orderId: string, input: AssignDriverVehicleInput): Promise<void> {
    if (!orderId) throw new Error('assign.errors.no_order');
    if (!input.driverId) throw new Error('assign.errors.no_driver');
    if (!input.vehicleId) throw new Error('assign.errors.no_vehicle');

    const patch: Record<string, unknown> = {
      assignedDriverId: input.driverId,
      assignedVehicleId: input.vehicleId,
      currentVehicleId: input.vehicleId,
      status: 'assigned',
      driverActions: {}, // reset prior accept/reject on (re)assignment
      updatedAt: serverTimestamp(),
    };
    if (input.driverName) patch.assignedDriverName = input.driverName;
    if (input.vehiclePlate) patch.assignedVehiclePlate = input.vehiclePlate;
    if (input.route) patch.route = input.route;
    if (input.cargo) patch.cargo = input.cargo;
    if (input.notes !== undefined) patch.notes = input.notes;
    if (input.customer) patch.customer = input.customer;

    try {
      const batch = writeBatch(db);
      // 1) The order — authoritative assignment (companyId untouched / immutable).
      batch.update(doc(db, 'orders', orderId), patch);
      // 2) Mirror the link onto the truck so the fleet map / vehicles page render
      //    the driver, the driver app can read the vehicle, and the location CF's
      //    authz path 1 (vehicle.currentDriverId === uid) can succeed.
      batch.update(doc(db, 'vehicles', input.vehicleId), {
        currentDriverId: input.driverId,
        currentOrderId: orderId,
        updatedAt: serverTimestamp(),
      });
      // 3) On a re-assign to a different truck, release the previous one so it
      //    never shows a stale driver.
      if (input.previousVehicleId && input.previousVehicleId !== input.vehicleId) {
        batch.update(doc(db, 'vehicles', input.previousVehicleId), {
          currentDriverId: null,
          currentOrderId: null,
          updatedAt: serverTimestamp(),
        });
      }
      await batch.commit();
    } catch (error) {
      console.error('Chyba pri priraďovaní vodiča:', error);
      throw new Error('assign.errors.update_failed');
    }
  }
}

export const orderService = new OrderService();
